import { Component, Inject, inject, OnInit } from '@angular/core';
import { RouterOutlet, ActivatedRoute, NavigationStart } from '@angular/router';
import { MenuComponent } from "./menu/feature/menu.component";
import { LoadingComponent } from './loading/feature/loading.component';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { routeTransition } from './route-transition';
import { Router, RoutesRecognized } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FooterComponent } from './core/footer/feature/footer.component';
import { Store } from '@ngrx/store';
import { AuthService } from './data-services/auth.service';
import { MiscDataService } from './data-services/misc.service';
import { environment } from '../environments/environment';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faFacebook, faGoogle, faPaypal, faStripe, faStripeS } from '@fortawesome/free-brands-svg-icons';
import { faBed, faCircleCheck, faCircleQuestion, faDoorOpen, faEdit, faLock, faMinus, faPenToSquare, faSpaghettiMonsterFlying, faStopwatch, faTicket, faTrash, faTriangleExclamation, faUtensils, faXmarkCircle, faBan, faXmark, faSpinner, faChevronUp, faChevronDown, faCheck, faCirclePlus, faBurger, faPizzaSlice, faNewspaper, faBars, faWarning, faRightToBracket, faCreditCard, faUser } from '@fortawesome/free-solid-svg-icons';
import { LoggingService } from './data-services/logging.service';
import { NgcCookieConsentService, NgcStatusChangeEvent } from 'ngx-cookieconsent';
import { GoogleAnalyticsInitializer, GtagFn, IGoogleAnalyticsSettings, NGX_GOOGLE_ANALYTICS_ROUTING_SETTINGS_TOKEN, NGX_GTAG_FN } from 'ngx-google-analytics';
import Clarity from '@microsoft/clarity';
import { UserActions } from './data-services/stores/actions/user.actions';
import { AdminMenuComponent } from "./core/admin-menu/feature/admin-menu.component";
import { selectUserDetails } from './data-services/stores/selectors/user.selectors';
import { Toast } from 'primeng/toast';

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, MenuComponent, LoadingComponent, FooterComponent, AdminMenuComponent, Toast],
    templateUrl: './app.component.html',
    styleUrl: './app.component.css',
    animations: [
        routeTransition,
        trigger('readyState', [
            state('fadeIn', style({
                opacity: 1,
            })),
            state('fadeOut', style({
                opacity: 0,
            })),
            transition('* => fadeOut', [animate('0.3s')]),
            transition('* => fadeIn', [animate('0.3s')]),
        ])
  ],
  providers: [
    ConfirmationService,
    LoggingService
    ]
})
export class AppComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _messageService = inject(MessageService);
  private readonly _router = inject(Router);
  private readonly _miscService = inject(MiscDataService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _store = inject(Store);
  private readonly _iconLib = inject(FaIconLibrary);
  


  private currentRoute: ActivatedRoute;
  isLoggedIn: boolean = false;
  displaySidepanel: boolean = false;
  displayFooter: boolean = true;
  private routeData: any;
  user$ = this._store.select(selectUserDetails);
  inPreviewMode: boolean = environment.preview
  isPreviewUser: boolean = this._authService.isPreviewUser;
  isAdminUser: boolean = this._authService.canAccessAdmin();
  

  constructor(
    private readonly _loggingService: LoggingService,
    private readonly _ccService: NgcCookieConsentService,
    @Inject(NGX_GOOGLE_ANALYTICS_ROUTING_SETTINGS_TOKEN) private readonly googleAnalyticsSettings: IGoogleAnalyticsSettings,
    @Inject(NGX_GTAG_FN) private readonly gtagFn: GtagFn,
    @Inject(Document) private readonly document: Document
  ) {


    this._iconLib.addIcons(faFacebook,faGoogle,faTriangleExclamation,faUser,faLock,faEdit,faTrash, faCircleCheck, faPaypal, faDoorOpen,faXmarkCircle, faCircleQuestion,faTicket,faSpaghettiMonsterFlying,faStopwatch,faUtensils,faBed,faMinus,faTicket, faPenToSquare, faBan, faXmark, faSpinner, faChevronDown,faChevronUp, faCheck, faCirclePlus, faBurger, faPizzaSlice,faNewspaper, faBars, faPaypal, faStripeS, faWarning, faRightToBracket,faCreditCard )

    this._miscService.checkConnectionStatus().subscribe(result => this.ready = result)
    this.currentRoute = this._route;
    this._router.events.subscribe((data) => {
      if (data instanceof RoutesRecognized) {
        if (this.inPreviewMode) {
          if (data.state.root.firstChild?.url[0]?.path !== 'preview') {
            if (!this.isLoggedIn || !this.isPreviewUser) {
              this._router.navigateByUrl('/preview')
            }
            if (this.isLoggedIn && !this.isPreviewUser) {
              this._messageService.add({severity:'error', detail:'You do not have permission to view the preview version of the website', summary:'Login Error'})
              this._router.navigateByUrl('/preview')
            }
          }
        }
        this.routeData = data.state.root.firstChild?.data;
        this.displaySidepanel = this.routeData?.hasOwnProperty("sidebar") ? this.routeData.sidebar : true;
        this.displayFooter = this.routeData?.hasOwnProperty("footer") ? this.routeData.footer : true;
      }
    });
    this._authService.authChanged.subscribe(result => {
      if (environment.preview && result) {
        if (!this._authService.isPreviewUser) {
          this._authService.logout();
        }
      }
      this.isLoggedIn = result;
      this.isPreviewUser = this._authService.isPreviewUser;

    })

    this.isLoggedIn = this._authService.checkTokenExpiry();
    if (this.isLoggedIn){
      this._store.dispatch(UserActions.loadUser({ refresh: false }))
    }
    
  }

  ngOnInit(): void {
    this._ccService.statusChange$.subscribe((event: NgcStatusChangeEvent) => {
      if (event.status === 'allow') {
        this.initializeTracking();
      }
      else if (event.status === 'deny') {
        this.cleanupTracking();
      }
    })
  }

  title = 'LRPTickets.Angular';
  ready: boolean = false;

  private initializeTracking(): void {
    if (this._ccService.hasConsented()) {
      GoogleAnalyticsInitializer(this.googleAnalyticsSettings, this.gtagFn, this.document);
      Clarity.init(environment.microsoft.CLARITY)
    }
  }

  private cleanupTracking(): void {
    window.location.reload()
  }
}
